import React from "react";
import Paper from "@mui/material/Paper";
import { makeStyles } from "tss-react/mui";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";

const useStyles = makeStyles()((theme) => ({
  DataTableContainer: {
    borderRadius: 4,
    backgroundColor: "#FFFFFF",
    padding: "5px 0px 0px",
    overflowX: "auto",
    "& ::-webkit-scrollbar-track": {
      marginTop: "36px",
    },
  },
  TableHead: {
    "& .MuiTableCell-root": {
      color: "rgba(0, 0, 0, 0.87)",
      padding: "6px 16px",
      height: "37px",
    },
  },
  TableBody: {
    "& .MuiOutlinedInput-input": {
      padding: "4px 10px",
    },
    "& .MuiTableCell-root": {
      padding: "6px 16px",
      borderBottom: "none",
      wordBreak: "break-word",
    },
  },
  TableRow: {
    // Remove border from body's rows
    "&:last-child td": {
      border: 0,
    },
    "& .MuiSvgIcon-root": {
      fontSize: "24px",
    },
  },
  IconHolder: {
    borderRadius: "50px",
    margin: "0",
    padding: "5px 5px",
    fontSize: "19px",
    "&.Mui-disabled": {
      color: "#00000042 !important",
    },
  },
}));

const TableWithStickHeader = React.memo(
  ({
    loading,
    tableMaxHeight,
    tableMaxWidth,
    columns,
    tableData,
    textMaxLength,
    noDataColspan,
    handleTextChange,
    handleEditClick,
    handleDeleteClick,
    noDataMessage,
    rowHover = false,
    rowHeight = 33,
  }) => {
    const { classes } = useStyles();

    return (
      <Paper sx={{ width: "100%", overflow: "hidden" }} className={classes.DataTableContainer}>
        <TableContainer
          sx={{
            maxHeight: tableMaxHeight || "none",
            maxWidth: tableMaxWidth || "100%",
            flexGrow: 1,
          }}
        >
          <Table stickyHeader aria-label="sticky table" size="small">
            <TableHead className={classes.TableHead}>
              <TableRow>
                {columns.map((header, index) => (
                  <TableCell
                    key={index}
                    style={header.width ? { width: header.width } : {}}
                    align={header.align || "inherit"}
                  >
                    {header.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody className={classes.TableBody}>
              {!loading && tableData.length ? (
                tableData.map((row, key) => (
                  <TableRow
                    key={key}
                    className={classes.TableRow}
                    sx={{ cursor: rowHover ? "pointer" : "text", height: rowHeight }}
                    hover={rowHover || false}
                  >
                    {row.map((cell, index) => (
                      <TableCell
                        component="th"
                        key={index}
                        scope="row"
                        align={cell.align || "inherit"}
                        style={columns[index].width ? { width: `${columns[index].width} !important` } : {}}
                      >
                        {cell.editable ? (
                          <TextField
                            inputProps={textMaxLength ? { maxLength: textMaxLength } : ""}
                            value={cell.value || ""}
                            sx={cell.textWidth ? { width: cell.textWidth } : ""}
                            name={`${columns[index].name}[]`}
                            onChange={(e) => handleTextChange(e, key, index)}
                          />
                        ) : cell.value ? (
                          cell.status != null ? (
                            <div style={{ display: "flex" }}>
                              {cell.value}
                              <span style={{ display: "flex", alignItems: "center", marginLeft: "auto" }}>
                                {cell.status === 0 ? (
                                  <Chip
                                    icon={<CancelOutlinedIcon sx={{ padding: "2px" }} />}
                                    label="Inactive"
                                    color="error"
                                    size="small"
                                    variant="outlined"
                                  />
                                ) : (
                                  <></>
                                )}
                              </span>
                            </div>
                          ) : (
                            cell.value
                          )
                        ) : cell.editIcon && cell.deleteIcon ? (
                          <>
                            <IconButton
                              className={classes.IconHolder}
                              color="error"
                              onClick={() => handleEditClick(key)}
                              disabled={cell.disabled}
                            >
                              {cell.editIcon}
                            </IconButton>
                            <IconButton
                              className={classes.IconHolder}
                              style={{ marginLeft: "5px" }}
                              color="error"
                              onClick={() => handleDeleteClick(key)}
                              disabled={cell.disabled}
                            >
                              {cell.deleteIcon}
                            </IconButton>
                          </>
                        ) : (
                          (cell.editIcon ? (
                            <IconButton
                              className={classes.IconHolder}
                              color="error"
                              onClick={() => handleEditClick(key)}
                              disabled={cell.disabled}
                            >
                              {cell.editIcon}
                            </IconButton>
                          ) : (
                            ""
                          ),
                          cell.deleteIcon ? (
                            <IconButton
                              className={classes.IconHolder}
                              style={{ marginLeft: "5px" }}
                              color="error"
                              onClick={() => handleDeleteClick(key)}
                              disabled={cell.disabled}
                            >
                              {cell.deleteIcon}
                            </IconButton>
                          ) : (
                            ""
                          ))
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow className={classes.TableRow} sx={{ height: rowHeight }}>
                  <TableCell colSpan={noDataColspan} component="td" scope="row" align={"center"}>
                    {loading ? <CircularProgress /> : noDataMessage || "No data available"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  }
);

export default TableWithStickHeader;
